import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreditsOnCart } from '@hub/api/mutations';
import { PagePath } from '@monorepo/client-types';
import {
  CreditCartAsset,
  CreditEventsModal,
  CreditEventType,
  getActiveAssets,
} from '@monorepo/hub-common';
import {
  CartData,
  CartStep,
  CartType,
  OptionalAssets,
  ProductType,
  SupportedCurrencies,
} from '@monorepo/types';
import { useCreditEventsFromNotifications } from './use-credit-events-from-notifications';
import { useCreditEventsSeenStorage } from './use-credit-events-seen-storage';

interface CreditEvent {
  type: CreditEventType;
  creditAmount: number;
  notificationId?: string;
}

interface CreditEventsModalState {
  queue: CreditEvent[];
}

interface CreditEventsModalContextValue {
  openCreditEventsModal: (
    type: CreditEventType,
    creditAmount: number,
    notificationId?: string,
  ) => void;
  closeCreditEventsModal: () => void;
  hasActiveCreditEvents: boolean;
}

const CreditEventsModalContext =
  createContext<CreditEventsModalContextValue | null>(null);

const CREDIT_EVENT_PRIORITY: Record<CreditEventType, number> = {
  [CreditEventType.ReceivedCredit]: 0,
  [CreditEventType.ExpiringCredit]: 1,
};

const ASSET_LABEL: Record<OptionalAssets, string> = {
  [OptionalAssets.Forex]: 'CFDs',
  [OptionalAssets.Futures]: 'Futures',
  [OptionalAssets.DayTrade]: 'Day Trade',
  [OptionalAssets.Swing]: 'Swing',
  [OptionalAssets.QuickBuy]: 'Quick Buy',
};

const DEFAULT_STATE: CreditEventsModalState = {
  queue: [],
};

const buildCartAssets = (cart: CartType): CreditCartAsset[] => {
  return getActiveAssets(cart).map((asset) => {
    const assetData = cart[asset];
    const firstProgram = assetData?.items.find(
      (item) => item.productType === ProductType.Program,
    );
    const meta =
      firstProgram?.productType === ProductType.Program
        ? (firstProgram.metadata as {
            groupName?: string;
            buyingPower?: number;
          })
        : undefined;

    return {
      asset,
      assetLabel: ASSET_LABEL[asset],
      groupName: meta?.groupName,
      buyingPower: meta?.buyingPower,
      totalPrice: assetData?.totalPrice ?? 0,
      tradingSystemLabel: ASSET_LABEL[asset],
      currency: cart.currency ?? SupportedCurrencies.USD,
    };
  });
};

interface CreditEventsModalProviderProps extends PropsWithChildren {
  cartData: CartData;
}

export const CreditEventsModalProvider = ({
  children,
  cartData,
}: CreditEventsModalProviderProps) => {
  const [modalState, setModalState] =
    useState<CreditEventsModalState>(DEFAULT_STATE);
  const navigate = useNavigate();
  const creditsOnCart = useCreditsOnCart();
  const { markNotificationSeen } = useCreditEventsSeenStorage();

  const openCreditEventsModal = useCallback(
    (type: CreditEventType, creditAmount: number, notificationId?: string) => {
      if (notificationId) {
        markNotificationSeen(notificationId);
      }
      setModalState((prev) => {
        const newItem: CreditEvent = { type, creditAmount, notificationId };
        const filteredQueue = prev.queue.filter((item) => item.type !== type);
        const newQueue = [...filteredQueue, newItem].sort(
          (a, b) =>
            CREDIT_EVENT_PRIORITY[a.type] - CREDIT_EVENT_PRIORITY[b.type],
        );
        return { queue: newQueue };
      });
    },
    [markNotificationSeen],
  );

  const closeCreditEventsModal = useCallback(() => {
    setModalState((prev) => ({ queue: prev.queue.slice(1) }));
  }, []);

  const handleRedeem = useCallback(async () => {
    const cart = cartData?.cart;
    const activeAssets = cart ? getActiveAssets(cart) : [];

    if (activeAssets.length > 0) {
      const firstAsset = activeAssets[0] as OptionalAssets;

      try {
        for (const asset of activeAssets) {
          await creditsOnCart.mutateAsync({ asset, useCredits: true });
        }
        closeCreditEventsModal();
        navigate(
          `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${firstAsset}`,
        );
      } catch (err) {
        console.error(err);
      }
    } else {
      closeCreditEventsModal();
      navigate(`${PagePath.Purchase}?step=${CartStep.Programs}`);
    }
  }, [cartData, creditsOnCart, closeCreditEventsModal, navigate]);

  const handleRedeemForAsset = useCallback(
    async (asset: OptionalAssets) => {
      try {
        await creditsOnCart.mutateAsync({ asset, useCredits: true });
        closeCreditEventsModal();
        navigate(
          `${PagePath.Purchase}?step=${CartStep.Billing}&asset=${asset}`,
        );
      } catch (err) {
        console.error(err);
      }
    },
    [creditsOnCart, closeCreditEventsModal, navigate],
  );

  const cartAssets = useMemo(
    () => (cartData?.cart ? buildCartAssets(cartData.cart) : []),
    [cartData?.cart],
  );

  useCreditEventsFromNotifications(openCreditEventsModal);

  const currentEvent = modalState.queue[0];
  const isOpen = modalState.queue.length > 0;

  const hasActiveCreditEvents = modalState.queue.length > 0;

  return (
    <CreditEventsModalContext.Provider
      value={{
        openCreditEventsModal,
        closeCreditEventsModal,
        hasActiveCreditEvents,
      }}
    >
      {children}
      <CreditEventsModal
        type={currentEvent?.type ?? CreditEventType.ReceivedCredit}
        open={isOpen}
        onClose={closeCreditEventsModal}
        creditAmount={currentEvent?.creditAmount ?? 0}
        onRedeem={handleRedeem}
        cartAssets={cartAssets}
        onRedeemForAsset={handleRedeemForAsset}
      />
    </CreditEventsModalContext.Provider>
  );
};

export const useCreditEventsModal = () => {
  const context = useContext(CreditEventsModalContext);

  if (!context) {
    throw new Error(
      'useCreditEventsModal must be used within CreditEventsModalProvider',
    );
  }

  return context;
};
